import { useState, type FormEvent } from 'react'
import { ChevronRight, Send } from 'lucide-react'
import { goldAlpha, tokens } from '@/theme'
import type { InitiativeSummary } from '@/domain/types'
import { formatCompactAge } from '@/domain/relativeTime'
import { useKeystoneStats, useRecentInitiatives } from '@/api/hooks/keystoneInitiatives'
import { STAGE_GROUPS, groupCount } from '@/domain/funnel'
import { KeystoneScene } from '@/components/visual/KeystoneScene'
import { MOBILE_MAX } from '@/components/layout/responsive'
import { StatusPill } from '@/components/StatusPill'
import { ErrorPanel, SkeletonBar, cycle, pulseRow } from '@/components/feedback'

/**
 * Home — the front door of Keystone.
 *
 * Implements the "2a Merged home" exploration from the Home Explorations
 * design doc: a dark ink hero carrying the dimensional mark and the Ask
 * Keystone box, a stats band reading straight off the
 * initiative counts, then recently-updated initiatives beside the entry
 * point to the ideas board.
 *
 * Every number and row on this page is live API data — the design's figures
 * (24 initiatives, 12 in progress) were placeholders. Each region degrades
 * on its own: a failed stats query dashes out the band without touching the
 * initiative list, and vice versa.
 */

interface Props {
  onOpenInitiative: (initiativeId: string) => void
  /** The ideas board — the funnel's front door, and the hero's main CTA. */
  onOpenIdeas: () => void
  /** Hands the question to the chat surface and routes there. */
  onAsk: (question: string) => void
}

const RECENT_LIMIT = 3

/**
 * Two deliberate rows rather than one wrapping strip: the design pairs a long
 * prompt with a short one on each line so the chips balance at the card's
 * 600px, and each chip stays on a single line.
 */
const PROMPT_ROWS = [
  ['List my initiatives', 'What changed this week?'],
  ['Which initiatives touch demand forecasting?'],
] as const

/**
 * The headline is two tiers — a quiet lead-in over the payoff. Both are
 * clamped rather than fixed: at full size "intelligent enterprise." overruns
 * a phone viewport, and the design canvas only ever renders at desktop width.
 *
 * The upper bounds sit under the design's own 36/58: at those figures the
 * dark band plus the stats under it filled a laptop viewport on its own, and
 * the paper section below was a sliver. The band is the front door, not the
 * page.
 */
const HEADLINE_TOP = 'clamp(24px, 2.8vw, 30px)'
const HEADLINE_BOTTOM = 'clamp(32px, 4.4vw, 46px)'

/**
 * Same reason: the mark is ambient and it was one of the two things setting
 * the band's height — the grid is only as tall as its taller column, so
 * trimming the copy alone would have left the scene holding the old height.
 */
const MARK_HEIGHT = 300

/**
 * Two-column grids collapse below 980px. Inline styles can't express media
 * queries, and the app carries no stylesheet — so, following the pattern
 * App.tsx already uses for its keyframes, the handful of responsive rules
 * live in a scoped <style> block. Everything else stays inline.
 *
 * The stat figures are sized here rather than inline because they are the
 * one thing on this page that has to shrink twice: once for the band's
 * height on a laptop, again for six of them side by side on a phone.
 */
const homeStyles = `
.keystone-home-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 48px;
  align-items: center;
}
.keystone-home-lower-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 40px;
  align-items: stretch;
}
.keystone-home-stat-value { font-size: 30px; }
.keystone-home-stat-value--lead { font-size: 40px; }
/* One prompt per line, as the design pairs them — until the longest chip is
   wider than the card, below which it has to be allowed to wrap. */
.keystone-home-chip { white-space: nowrap; }
@media (max-width: 700px) {
  .keystone-home-chip { white-space: normal; }
}
@media (max-width: 980px) {
  .keystone-home-hero-grid,
  .keystone-home-lower-grid { grid-template-columns: minmax(0, 1fr); gap: 32px; }
  .keystone-home-mark { display: none; }
}
@media (max-width: ${MOBILE_MAX}px) {
  .keystone-home-hero-grid { padding-top: 26px !important; }
  .keystone-home-stats { gap: 24px !important; row-gap: 18px !important; padding-top: 18px !important; }
  .keystone-home-stat-value { font-size: 24px; }
  .keystone-home-stat-value--lead { font-size: 32px; }
  /* Two rules of the same colour on a wrapped band read as a stray mark. */
  .keystone-home-stat-rule { display: none; }
}
`

export function HomeView({ onOpenInitiative, onOpenIdeas, onAsk }: Props) {
  const stats = useKeystoneStats()
  const recentQuery = useRecentInitiatives(RECENT_LIMIT)
  const recent = recentQuery.status === 'success' ? recentQuery.data.data : []

  return (
    <>
      <style>{homeStyles}</style>

      {/* ————— Dark hero ————— */}
      <section style={{ position: 'relative', background: tokens.inkPanel, overflow: 'hidden' }}>
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(ellipse at 78% 24%, ${goldAlpha(0.14)} 0%, transparent 55%)`,
            pointerEvents: 'none',
          }}
        />

        <div
          className="keystone-home-hero-grid keystone-pad"
          style={{
            position: 'relative',
            maxWidth: tokens.containerMax,
            margin: '0 auto',
            padding: `26px ${tokens.containerPadX}px 0`,
          }}
        >
          <div>
            <div
              style={{
                width: 56,
                height: 2,
                background: tokens.gold,
                marginBottom: 12,
                transformOrigin: 'left',
                animation: `barGrow ${tokens.durSlow} ${tokens.easeStandard} both`,
              }}
            />
            <div
              style={{
                fontFamily: tokens.fontDisplay,
                fontSize: 11.5,
                fontWeight: tokens.weightDemi,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: tokens.inkPanelText,
                marginBottom: 10,
                animation: `slideUp ${tokens.durSlow} ${tokens.easeStandard} both`,
                animationDelay: '80ms',
              }}
            >
              Trelleborg Industrial Solutions · Internal
            </div>
            <h1
              style={{
                fontFamily: tokens.fontDisplay,
                letterSpacing: '-0.02em',
                lineHeight: 1.05,
                margin: 0,
              }}
            >
              <span
                style={{
                  display: 'block',
                  fontSize: HEADLINE_TOP,
                  fontWeight: tokens.weightMedium,
                  color: tokens.inkPanelText,
                  animation: `slideUp ${tokens.durSlow} ${tokens.easeStandard} both`,
                  animationDelay: '140ms',
                }}
              >
                Engineering the
              </span>
              <span
                style={{
                  display: 'block',
                  fontSize: HEADLINE_BOTTOM,
                  fontWeight: tokens.weightDemi,
                  color: tokens.paper,
                  animation: `slideUp ${tokens.durSlow} ${tokens.easeStandard} both`,
                  animationDelay: '220ms',
                }}
              >
                intelligent enterprise.
              </span>
            </h1>
            <p
              style={{
                fontSize: 14,
                lineHeight: 1.6,
                color: tokens.inkPanelText,
                margin: '12px 0 0',
                maxWidth: 560,
                animation: `slideUp ${tokens.durSlow} ${tokens.easeStandard} both`,
                animationDelay: '300ms',
              }}
            >
              Keystone is the single point of access to every initiative in Trelleborg Industrial
              Solutions — a system, a process change, an integration or an experiment all belong
              here on the same terms.
            </p>

            <AskKeystoneCard onAsk={onAsk} />
          </div>

          <div className="keystone-home-mark">
            <KeystoneScene variant="mark" height={MARK_HEIGHT} />
          </div>
        </div>

        {/* ————— Stats band ————— */}
        <div
          className="keystone-pad"
          style={{
            position: 'relative',
            maxWidth: tokens.containerMax,
            margin: '0 auto',
            padding: `16px ${tokens.containerPadX}px 20px`,
          }}
        >
          <div
            className="keystone-home-stats"
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 56,
              rowGap: 24,
              flexWrap: 'wrap',
              borderTop: `1px solid ${tokens.inkPanelLine}`,
              paddingTop: 18,
            }}
          >
            <Stat
              value={stats.status === 'success' ? stats.data.total : null}
              label="Initiatives"
              lead
            />
            <StatRule />
            {/* The four stages the site shows. Close sums both closed codes:
                its outcome is a field inside it, not a stage of its own. */}
            {STAGE_GROUPS.map(({ key, label }) => (
              <Stat
                key={key}
                value={stats.status === 'success' ? groupCount(stats.data.byStage, key) : null}
                label={label}
                dot={tokens.stageOnDark[key === 'closed' ? 'closed_success' : key]}
              />
            ))}
            <StatRule />
            <Stat
              value={stats.status === 'success' ? stats.data.openIdeas : null}
              label="Ideas on the board"
              dot={tokens.gold}
              onClick={onOpenIdeas}
            />
          </div>
        </div>
      </section>

      {/* ————— Recently updated + the ideas board ————— */}
      <div
        className="keystone-home-lower-grid keystone-pad"
        style={{
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          // Bottom stops short of the design's 56px because SiteFooter carries
          // a 96px top margin of its own on every route.
          padding: `40px ${tokens.containerPadX}px 8px`,
        }}
      >
        <section style={{ display: 'flex', flexDirection: 'column' }}>
          <SectionLabel>Recently updated</SectionLabel>

          {(recentQuery.status === 'idle' || recentQuery.status === 'loading') && (
            <RecentSkeleton />
          )}

          {recentQuery.status === 'error' && (
            <ErrorPanel error={recentQuery.error} maxWidth="100%" />
          )}

          {recentQuery.status === 'success' && recent.length === 0 && (
            <p
              style={{
                fontSize: tokens.sizeBody,
                color: tokens.ink3,
                background: tokens.paper2,
                border: `1px solid ${tokens.line}`,
                borderRadius: tokens.radiusMd,
                padding: '20px 22px',
                margin: 0,
              }}
            >
              No initiatives yet. Once one is created it appears here.
            </p>
          )}

          {recentQuery.status === 'success' && recent.length > 0 && (
            <ul
              style={{
                listStyle: 'none',
                flex: 1,
                display: 'grid',
                gap: 8,
                gridAutoRows: '1fr',
                margin: 0,
                padding: 0,
              }}
            >
              {recent.map(initiative => (
                <RecentRow
                  key={initiative.id}
                  initiative={initiative}
                  onClick={() => onOpenInitiative(initiative.id)}
                />
              ))}
            </ul>
          )}
        </section>

        <section>
          <SectionLabel>Where initiatives come from</SectionLabel>
          <IdeasBoardCard onOpen={onOpenIdeas} />
        </section>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Ask Keystone — the hero's chat entry point
// ---------------------------------------------------------------------------

function AskKeystoneCard({ onAsk }: { onAsk: (question: string) => void }) {
  const [value, setValue] = useState('')
  const [sendHover, setSendHover] = useState(false)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const question = value.trim()
    if (!question) return
    setValue('')
    onAsk(question)
  }

  return (
    <form
      onSubmit={submit}
      style={{
        marginTop: 18,
        background: tokens.paper,
        borderRadius: tokens.radiusLg,
        boxShadow: '0 20px 48px -20px rgba(0,0,0,0.5)',
        padding: '14px 16px 12px 18px',
        maxWidth: 600,
        animation: `slideUp ${tokens.durSlow} ${tokens.easeStandard} both`,
        animationDelay: '380ms',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <input
          type="text"
          value={value}
          onChange={e => setValue(e.target.value)}
          aria-label="Ask Keystone"
          placeholder="Ask Keystone — initiatives, owners, ideas…"
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontFamily: tokens.fontBody,
            fontSize: tokens.sizeBody,
            color: tokens.ink,
          }}
        />
        <button
          type="submit"
          aria-label="Ask Keystone"
          onMouseEnter={() => setSendHover(true)}
          onMouseLeave={() => setSendHover(false)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: 40,
            height: 40,
            border: 'none',
            borderRadius: tokens.radiusMd,
            background: sendHover ? tokens.goldDark : tokens.gold,
            color: tokens.paper,
            cursor: 'pointer',
            transition: `background ${tokens.durFast} ${tokens.easeStandard}`,
          }}
        >
          <Send size={16} strokeWidth={1.8} />
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          marginTop: 12,
          paddingTop: 10,
          borderTop: `1px solid ${tokens.line}`,
        }}
      >
        {PROMPT_ROWS.map(row => (
          <div key={row[0]} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {row.map(prompt => (
              <PromptChip key={prompt} label={prompt} onClick={() => onAsk(prompt)} />
            ))}
          </div>
        ))}
      </div>
    </form>
  )
}

function PromptChip({ label, onClick }: { label: string; onClick: () => void }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      className="keystone-home-chip"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        fontFamily: tokens.fontBody,
        fontSize: 12,
        color: tokens.ink2,
        background: hover ? tokens.paper2 : tokens.paper,
        border: `1px solid ${hover ? tokens.line2 : tokens.line}`,
        borderRadius: 999,
        padding: '5px 12px',
        cursor: 'pointer',
        textAlign: 'left',
        transition: `all ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      {label}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Stats band
// ---------------------------------------------------------------------------

function Stat({
  value,
  label,
  dot,
  lead,
  onClick,
}: {
  value: number | null
  label: string
  dot?: string | undefined
  lead?: boolean | undefined
  /** Makes the figure a link. Only the ideas count uses it. */
  onClick?: (() => void) | undefined
}) {
  const Wrapper = onClick ? 'button' : 'div'
  return (
    <Wrapper
      {...(onClick
        ? {
            type: 'button' as const,
            onClick,
            style: {
              background: 'transparent',
              border: 'none',
              padding: 0,
              textAlign: 'left' as const,
              cursor: 'pointer',
            },
          }
        : {})}
    >
      <div
        // Size lives in the stylesheet — see homeStyles.
        className={`keystone-home-stat-value${lead ? ' keystone-home-stat-value--lead' : ''}`}
        style={{
          fontFamily: tokens.fontDisplay,
          fontWeight: tokens.weightDemi,
          lineHeight: 1,
          color: lead ? tokens.gold : tokens.paper,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value === null ? '—' : value}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          marginTop: 8,
        }}
      >
        {dot && (
          <span
            aria-hidden
            style={{ width: 7, height: 7, borderRadius: 999, background: dot, flexShrink: 0 }}
          />
        )}
        <span
          style={{
            fontFamily: tokens.fontDisplay,
            fontSize: 11,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: tokens.inkPanelText,
          }}
        >
          {label}
        </span>
      </div>
    </Wrapper>
  )
}

/** The hairline separating the portfolio total from the per-stage counts. */
function StatRule() {
  return (
    <div
      className="keystone-home-stat-rule"
      aria-hidden
      style={{ width: 1, alignSelf: 'stretch', background: tokens.inkPanelLine }}
    />
  )
}

// ---------------------------------------------------------------------------
// Recently updated
// ---------------------------------------------------------------------------

function RecentRow({
  initiative,
  onClick,
}: {
  initiative: InitiativeSummary
  onClick: () => void
}) {
  const [hover, setHover] = useState(false)

  return (
    <li style={{ display: 'grid' }}>
      <button
        className="keystone-sm-row"
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          alignItems: 'center',
          gap: 20,
          width: '100%',
          height: '100%',
          textAlign: 'left',
          background: hover ? tokens.paper2 : tokens.paper,
          // Longhand on purpose: the hover restyle rewrites the side borders,
          // and a `border` shorthand next to `borderTop` would take the gold
          // rule with it on re-render.
          borderTop: `2px solid ${tokens.gold}`,
          borderRight: `1px solid ${hover ? tokens.line2 : tokens.line}`,
          borderBottom: `1px solid ${hover ? tokens.line2 : tokens.line}`,
          borderLeft: `1px solid ${hover ? tokens.line2 : tokens.line}`,
          padding: '16px 20px',
          cursor: 'pointer',
          fontFamily: tokens.fontDisplay,
          transition: `all ${tokens.durFast} ${tokens.easeStandard}`,
        }}
      >
        <span style={{ minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontSize: 15,
              fontWeight: tokens.weightDemi,
              color: tokens.ink,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {initiative.title}
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 12,
              color: tokens.ink3,
              marginTop: 3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {initiative.owner.displayName}
          </span>
        </span>

        <StatusPill stage={initiative.stage} />

        <span
          // The section is headed "Recently updated"; on a phone the age of
          // each row is the first thing that can go to keep the name legible.
          className="keystone-sm-hide"
          style={{
            fontSize: 12,
            color: hover ? tokens.ink3 : tokens.ink4,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
          title={`Last updated ${initiative.modifiedAt}`}
        >
          {formatCompactAge(initiative.modifiedAt)}
        </span>
      </button>
    </li>
  )
}

// ---------------------------------------------------------------------------
// The ideas board
// ---------------------------------------------------------------------------

function IdeasBoardCard({ onOpen }: { onOpen: () => void }) {
  const [hover, setHover] = useState(false)
  const [ctaHover, setCtaHover] = useState(false)

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: tokens.paper,
        borderTop: `2px solid ${tokens.gold}`,
        borderRight: `1px solid ${tokens.line}`,
        borderBottom: `1px solid ${tokens.line}`,
        borderLeft: `1px solid ${tokens.line}`,
        padding: 30,
        boxShadow: hover ? '0 12px 32px -22px rgba(17,17,17,0.3)' : 'none',
        transition: `box-shadow ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      <NetworkGlyph />

      <h2
        style={{
          fontFamily: tokens.fontDisplay,
          fontSize: 22,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          lineHeight: 1.3,
          margin: '20px 0 12px',
        }}
      >
        Start on the ideas board
      </h2>
      <p
        style={{
          fontSize: 14,
          lineHeight: 1.65,
          color: tokens.ink3,
          margin: '0 0 26px',
        }}
      >
        Anyone can create an idea, anyone can vote. A shaper turns the good ones into initiatives.
      </p>

      <button
        type="button"
        onClick={onOpen}
        onMouseEnter={() => setCtaHover(true)}
        onMouseLeave={() => setCtaHover(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: ctaHover ? tokens.goldDark : tokens.gold,
          color: tokens.paper,
          border: 'none',
          borderRadius: tokens.radiusMd,
          padding: '12px 24px',
          fontFamily: tokens.fontDisplay,
          fontSize: 12,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          cursor: 'pointer',
          transition: `background ${tokens.durFast} ${tokens.easeStandard}`,
        }}
      >
        Open the board
        <ChevronRight size={14} strokeWidth={1.8} />
      </button>
    </div>
  )
}

/** A constellation — decorative, matches the hero's palette. */
function NetworkGlyph() {
  return (
    <svg
      width="120"
      height="64"
      viewBox="0 0 120 64"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <line x1="34" y1="18" x2="12" y2="46" stroke={tokens.gold} strokeWidth="1" opacity="0.45" />
      <line x1="34" y1="18" x2="42" y2="50" stroke={tokens.gold} strokeWidth="1" opacity="0.45" />
      <line x1="34" y1="18" x2="82" y2="12" stroke={tokens.gold} strokeWidth="1" opacity="0.3" />
      <line x1="82" y1="12" x2="104" y2="38" stroke={tokens.gold} strokeWidth="1" opacity="0.45" />
      <line x1="82" y1="12" x2="68" y2="46" stroke={tokens.gold} strokeWidth="1" opacity="0.45" />
      <circle cx="34" cy="18" r="7" fill={tokens.gold} />
      <circle cx="82" cy="12" r="7" fill={tokens.gold} />
      <circle cx="12" cy="46" r="4" fill={tokens.inkPanel} opacity="0.8" />
      <circle cx="42" cy="50" r="4" fill={tokens.stageOnDark.in_progress} />
      <circle cx="104" cy="38" r="4" fill={tokens.inkPanel} opacity="0.8" />
      <circle cx="68" cy="46" r="4" fill={tokens.inkPanel} opacity="0.8" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
      <span style={{ width: 32, height: 2, background: tokens.gold }} />
      <span
        style={{
          fontFamily: tokens.fontDisplay,
          fontSize: 11,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: tokens.gold,
        }}
      >
        {children}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Skeleton: the recent list's own rows with bars for the text, so the frame
// does not jump when the initiatives land.
// ---------------------------------------------------------------------------

const TITLE_WIDTHS = [230, 170, 260]
const titleWidth = cycle(TITLE_WIDTHS, 200)

function RecentSkeleton() {
  return (
    <ul
      aria-busy="true"
      style={{
        listStyle: 'none',
        flex: 1,
        display: 'grid',
        gap: 8,
        gridAutoRows: '1fr',
        margin: 0,
        padding: 0,
      }}
    >
      {Array.from({ length: RECENT_LIMIT }).map((_, i) => (
        <li key={i} data-skeleton="" style={{ display: 'grid', animation: pulseRow(i) }}>
          <div
            className="keystone-sm-row"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto auto',
              alignItems: 'center',
              gap: 20,
              background: tokens.paper,
              borderTop: `2px solid ${tokens.gold}`,
              borderRight: `1px solid ${tokens.line}`,
              borderBottom: `1px solid ${tokens.line}`,
              borderLeft: `1px solid ${tokens.line}`,
              padding: '16px 20px',
            }}
          >
            <span style={{ minWidth: 0, display: 'grid', gap: 8 }}>
              <SkeletonBar width={titleWidth(i)} height={16} pulse={false} />
              <SkeletonBar width={120} height={12} pulse={false} />
            </span>
            <SkeletonBar width={74} height={20} pulse={false} />
            <span className="keystone-sm-hide">
              <SkeletonBar width={28} height={12} pulse={false} />
            </span>
          </div>
        </li>
      ))}
    </ul>
  )
}
